import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../translations/translations';

// --- Configuration and Data Structure ---
const MERCHANT_API_KEY = process.env.REACT_APP_PAYME_SELLER_KEY || "YOUR_MERCHANT_API_KEY";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// --- Helper Hook to load PayMe script ---
const usePayMeScript = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (window.PayMe) {
      setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = "https://cdn.payme.io/hf/v1/hostedfields.js";
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);
  return loaded;
};

// --- Styles ---
const styles = {
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.10)',
    position: 'relative',
    zIndex: 10,
    border: '1px solid rgba(0, 0, 0, 0.07)',
  },
  secureHeader: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '16px 16px 0 0',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  secureHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  secureLockIcon: {
    fontSize: '15px',
  },
  secureHeaderText: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.3px',
  },
  secureHeaderBadge: {
    background: 'rgba(89, 181, 86, 0.2)',
    border: '1px solid rgba(89, 181, 86, 0.4)',
    borderRadius: '20px',
    padding: '3px 10px',
    color: '#7ecf7b',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.3px',
  },
  formWrapper: {
    padding: '24px 28px 28px',
  },
  walletContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  },
  applePayBtn: {
    flex: 1,
    padding: '14px 20px',
    background: 'linear-gradient(145deg, #1a1a1a 0%, #000000 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  },
  googlePayBtn: {
    flex: 1,
    padding: '14px 20px',
    background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
    color: '#3c4043',
    border: '1px solid #dadce0',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    gap: '16px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'linear-gradient(to right, transparent, #e0e0e0, transparent)',
  },
  dividerText: {
    color: '#888',
    fontSize: '14px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  fieldContainer: {
    marginBottom: '20px',
    position: 'relative',
    zIndex: 10,
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  hostedField: {
    height: '48px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px',
    padding: '0 16px',
    background: '#fff',
    transition: 'all 0.3s ease',
    position: 'relative',
    zIndex: 10,
  },
  rowFields: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
    position: 'relative',
    zIndex: 10,
  },
  halfField: {
    flex: 1,
  },
  statusMessage: {
    padding: '12px 16px',
    borderRadius: '10px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  statusLoading: {
    background: 'linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%)',
    color: '#1565c0',
    border: '1px solid #90caf9',
  },
  statusError: {
    background: 'linear-gradient(145deg, #ffebee 0%, #ffcdd2 100%)',
    color: '#c62828',
    border: '1px solid #ef9a9a',
  },
  statusSuccess: {
    background: 'linear-gradient(145deg, #e8f5e9 0%, #c8e6c9 100%)',
    color: '#2e7d32',
    border: '1px solid #a5d6a7',
  },
  submitBtn: {
    width: '100%',
    padding: '15px 24px',
    background: 'linear-gradient(135deg, #59b556 0%, #4ca350 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 16px rgba(89, 181, 86, 0.35)',
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  submitBtnDisabled: {
    background: 'linear-gradient(135deg, #ccc 0%, #bbb 100%)',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  secureFooter: {
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: '#aaa',
    fontSize: '11.5px',
  },
  secureFooterIcon: {
    fontSize: '12px',
    color: '#bbb',
  },
  inputField: {
    width: '100%',
    height: '48px',
    border: '2px solid #e8e8e8',
    borderRadius: '12px',
    padding: '0 16px',
    background: '#fff',
    fontSize: '16px',
    color: '#333',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
};

// --- The Digital Wallet Placeholder Component ---
const DigitalWalletButtons = ({ handleWalletPay }) => (
    <div className="digital-wallets" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
      {/* NOTE: The logic for generating the secure token from the browser must be implemented in handleWalletPay */}
      <button 
          type="button" 
          onClick={() => handleWalletPay('apple-pay')}
          style={{ flex: 1, padding: '10px', background: 'black', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
      >
         Pay
      </button>
      <button 
          type="button" 
          onClick={() => handleWalletPay('google-pay')}
          style={{ flex: 1, padding: '10px', background: 'black', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
      >
        G Pay
      </button>
    </div>
);

// --- Main Checkout Component ---
const PayMe3DSCheckout = ({ saleDetails, onSuccess }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRtl = language === 'he';
  
  const scriptLoaded = usePayMeScript();
  const [paymeInstance, setPaymeInstance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState(''); // 'loading', 'error', 'success'
  
  // Form fields with autofill from saleDetails
  const [buyerName, setBuyerName] = useState(
    saleDetails?.sale_name || 
    `${saleDetails?.payerFirstName || ''} ${saleDetails?.payerLastName || ''}`.trim() || 
    ''
  );
  const [buyerPhone, setBuyerPhone] = useState(saleDetails?.payerPhone || saleDetails?.sale_mobile || '');
  const [buyerEmail, setBuyerEmail] = useState(saleDetails?.payerEmail || saleDetails?.buyer_email || '');
  const [buyerSocialId, setBuyerSocialId] = useState(saleDetails?.buyer_social_id || '');

  // Helper to set status with type
  const setStatus = (msg, type = 'loading') => {
    setStatusMsg(msg);
    setStatusType(type);
  };

  // 1. Initialize PayMe when script loads
  useEffect(() => {
    if (scriptLoaded && !paymeInstance) {
      window.PayMe.create(MERCHANT_API_KEY, { testMode: false })
        .then((instance) => {
          setPaymeInstance(instance);
          const fields = instance.hostedFields();

          const options = {
            styles: {
              base: {
                color: '#333',
                fontSize: '16px',
                height: '100%',
                fontFamily: 'inherit',
              },
            },
          };

          fields.create(window.PayMe.fields.NUMBER, options).mount('#card-number-field');
          fields.create(window.PayMe.fields.EXPIRATION, options).mount('#card-expiration-field');
          fields.create(window.PayMe.fields.CVC, options).mount('#card-cvc-field');
        })
        .catch((err) => {
          console.error("PayMe Initialization Error:", err);
          setStatus(t.paymentSystemError, 'error');
        });
    }
  }, [scriptLoaded, paymeInstance, t.paymentSystemError]);

  // --- HANDLE PAY FUNCTION ---
  const handlePay = async (paymentData) => {
    setLoading(true);
    setStatus(t.generatingSale, 'loading');

    try {
      const saleGeneratePayload = {
        sale_price: paymentData.sale_price,
        currency: paymentData.currency || "ILS",
        product_name: paymentData.product_name,
        transaction_id: paymentData.transaction_id,
        sale_mobile: paymentData.sale_mobile,
        sale_name: paymentData.sale_name,
        sale_payment_method: paymentData.sale_payment_method || "credit-card",
        language: paymentData.language || "he",
      };

      const saleGenerateResponse = await fetch(`${API_URL}/sale-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saleGeneratePayload),
      });

      if (!saleGenerateResponse.ok) {
        throw new Error(`Sale generate failed: ${saleGenerateResponse.statusText}`);
      }

      const saleGenerateData = await saleGenerateResponse.json();

      if (saleGenerateData.status_code !== 0) {
        throw new Error(`${saleGenerateData.status_message || 'Unknown error'}`);
      }

      const paymeSaleId = saleGenerateData.payme_sale_id;
      if (!paymeSaleId) {
        throw new Error("No payme_sale_id returned from sale-generate");
      }

      setStatus(t.processingPayment, 'loading');

      const paySalePayload = {
        sale_price: String(paymentData.sale_price),
        currency: paymentData.currency || "ILS",
        buyer_email: paymentData.buyer_email,
        buyer_name: paymentData.buyer_name || paymentData.sale_name,
        buyer_social_id: paymentData.buyer_social_id,
        buyer_phone: paymentData.buyer_phone,
        payme_sale_id: paymeSaleId,
        buyer_key: paymentData.card_token,
      };

      const paySaleResponse = await fetch(`${API_URL}/pay-sale`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paySalePayload),
      });

      if (!paySaleResponse.ok) {
        throw new Error(`Pay sale failed: ${paySaleResponse.statusText}`);
      }

      const paySaleData = await paySaleResponse.json();

      if (paySaleData.payme_status === 'success' && paySaleData.status_error_code === 0) {
        setStatus(t.paymentSuccessful, 'success');
        return {
          success: true,
          data: paySaleData,
          saleId: paymeSaleId,
          saleCode: paySaleData.payme_sale_code,
        };
      } else {
        throw new Error(paySaleData.status_message || 'Payment failed');
      }

    } catch (error) {
      console.error('Payment error:', error);
      setStatus(`${t.paymentFailed}: ${error.message}`, 'error');
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setLoading(false);
    }
  };

  // --- DIGITAL WALLET HANDLER ---
  const handleDigitalWalletPayment = (method) => {
    const methodName = method === 'apple-pay' ? t.applePay : t.googlePay;
    setStatus(`${methodName}...`, 'loading');
    alert(`Initiating ${method}. This typically uses the browser's native API to obtain a token.`);
    setLoading(false);
    setStatusMsg('');
  };

  // Get status styles
  const getStatusStyle = () => {
    const base = styles.statusMessage;
    if (statusType === 'loading') return { ...base, ...styles.statusLoading };
    if (statusType === 'error') return { ...base, ...styles.statusError };
    if (statusType === 'success') return { ...base, ...styles.statusSuccess };
    return base;
  };

  return (
    <div style={{ ...styles.container, direction: isRtl ? 'rtl' : 'ltr' }}>
      {/* Secure header */}
      <div style={styles.secureHeader}>
        <div style={styles.secureHeaderLeft}>
          <span style={styles.secureLockIcon}>🔒</span>
          <span style={styles.secureHeaderText}>
            {isRtl ? 'תשלום מאובטח' : 'Secure Payment'}
          </span>
        </div>
        <span style={styles.secureHeaderBadge}>SSL</span>
      </div>

      <div style={styles.formWrapper}>
      {/* Hosted Fields Form */}
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (!paymeInstance) {
          setStatus(t.paymentSystemNotInit, 'error');
          return;
        }

        setLoading(true);
        setStatus(t.tokenizingCard, 'loading');

        try {
          // Parse name into first and last
          const nameParts = buyerName.trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          const tokenResult = await paymeInstance.tokenize({
            payerFirstName: firstName || saleDetails.payerFirstName,
            payerLastName: lastName || saleDetails.payerLastName,
            payerEmail: buyerEmail || saleDetails.payerEmail,
            total: saleDetails.total,
            payerPhone: buyerPhone || saleDetails.payerPhone,
            payerSocialId: buyerSocialId,
          });

          const payResult = await handlePay({
            sale_price: saleDetails.sale_price,
            currency: saleDetails.currency,
            product_name: saleDetails.product_name,
            transaction_id: saleDetails.transaction_id,
            sale_mobile: buyerPhone || saleDetails.sale_mobile,
            sale_name: buyerName || saleDetails.sale_name || `${saleDetails.payerFirstName} ${saleDetails.payerLastName}`,
            sale_payment_method: saleDetails.sale_payment_method || "credit-card",
            language: saleDetails.language || "he",
            buyer_email: buyerEmail || saleDetails.payerEmail || saleDetails.buyer_email,
            buyer_name: buyerName || saleDetails.buyer_name || saleDetails.sale_name,
            buyer_social_id: buyerSocialId,
            buyer_phone: buyerPhone || saleDetails.payerPhone,
            card_token: tokenResult.token,
          });
          if (payResult?.success && onSuccess) {
            onSuccess(payResult);
          }
        } catch (error) {
          console.error('Tokenization error:', error);
          setStatus(`${t.paymentFailed}: ${error.message}`, 'error');
          setLoading(false);
        }
      }}>
        {/* Personal Details Fields */}
        <div style={styles.rowFields}>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.fullName}
            </label>
            <input
              type="text"
              value={buyerName}
              onChange={(e) => setBuyerName(e.target.value)}
              style={{ ...styles.inputField, textAlign: isRtl ? 'right' : 'left' }}
              required
            />
          </div>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.socialId}
            </label>
            <input
              type="text"
              value={buyerSocialId}
              onChange={(e) => setBuyerSocialId(e.target.value)}
              style={{ ...styles.inputField, textAlign: isRtl ? 'right' : 'left' }}
              placeholder="000000000"
              maxLength={9}
              required
            />
          </div>
        </div>

        <div style={styles.rowFields}>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.phoneNumberShort}
            </label>
            <input
              type="tel"
              value={buyerPhone}
              onChange={(e) => setBuyerPhone(e.target.value)}
              style={{ ...styles.inputField, textAlign: isRtl ? 'right' : 'left' }}
              required
            />
          </div>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.emailShort}
            </label>
            <input
              type="email"
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
              style={{ ...styles.inputField, textAlign: isRtl ? 'right' : 'left' }}
              required
            />
          </div>
        </div>

        {/* Card Number Field */}
        <div style={styles.fieldContainer}>
          <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
            {t.cardNumber}
          </label>
          <div id="card-number-field" style={styles.hostedField}></div>
        </div>

        {/* Expiration & CVC Row */}
        <div style={styles.rowFields}>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.expiration}
            </label>
            <div id="card-expiration-field" style={styles.hostedField}></div>
          </div>
          <div style={styles.halfField}>
            <label style={{ ...styles.label, textAlign: isRtl ? 'right' : 'left' }}>
              {t.cvc}
            </label>
            <div id="card-cvc-field" style={styles.hostedField}></div>
          </div>
        </div>

        {/* Status Message */}
        {statusMsg && (
          <div style={getStatusStyle()}>
            {statusMsg}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !paymeInstance}
          style={{
            ...styles.submitBtn,
            ...(loading || !paymeInstance ? styles.submitBtnDisabled : {}),
          }}
          onMouseOver={(e) => {
            if (!loading && paymeInstance) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(89, 181, 86, 0.45)';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(89, 181, 86, 0.35)';
          }}
        >
          <span>🔒</span>
          <span>{loading ? t.processing : `${t.pay} ${saleDetails.sale_price / 100} ${saleDetails.currency}`}</span>
        </button>
      </form>

      {/* Secure footer */}
      <div style={styles.secureFooter}>
        <span style={styles.secureFooterIcon}>🛡️</span>
        <span>
          {isRtl
            ? 'מוצפן ומאובטח · מעובד על ידי PayMe'
            : 'Encrypted & secured · Processed by PayMe'}
        </span>
      </div>
      </div>
    </div>
  );
};

export default PayMe3DSCheckout;
